import { Point2D as Point2DInterface } from '@paragon/contract-library';
import { Direction2D, distanceEquals, Point2D } from '@paragon/geometry-library';
import { assertExhaustiveSwitch, roundToIncrement } from '@paragon/utilities';

import type {
  SnappingLevel,
  WorkLineInsertionSelection,
  WorkLineInsertionSettings,
} from '../../../store/slices/applicationSlice';
import { getDistanceSnap } from '../../../three/hooks/useMousePositionWithBasicInsertionSnapping';
import { EntitySnapTarget } from '../../layout-snapping/EntitySnapTarget';
import { snapAngleToDegreesWithOrthogonalBias } from '../../layout-snapping/point-snapping/angleSnapping';
import {
  getSnappingThreshold,
  MousePositionSnapType,
  SnapMousePositionResult,
} from '../../layout-snapping/point-snapping/snapMousePosition';
import { InsertionWorkLine } from './insertionWorkLine';

/** The full set of snap results captured at a cursor position during work line insertion. */
export interface WorkLineSnap {
  readonly unsnappedPoint: Point2D;

  readonly pointSnap: SnapMousePositionResult;
  readonly lineSnap: EntitySnapTarget | null;
}

/** Demotes point snaps that aren't relevant during work line insertion (grid, nearest, etc. — see
 *    `isTrueEntityPointSnapForWorkLineInsertion`) to an unsnapped result at the raw cursor position.
 */
export function normalizePointSnapForWorkLineInsertion(
  pointSnap: SnapMousePositionResult,
  unsnappedPoint: Point2D,
): SnapMousePositionResult {
  return isTrueEntityPointSnapForWorkLineInsertion(pointSnap.type) ? pointSnap : { type: null, point: unsnappedPoint };
}

/**
 * The single source of truth for the line-vs-point decision behind a scene click during work-line insertion click.
 */
export function classifyClickDuringWorkLineInsertion(snap: WorkLineSnap): WorkLineInsertionSelection {
  const pointSnap = normalizePointSnapForWorkLineInsertion(snap.pointSnap, snap.unsnappedPoint);

  if (
    pointSnap.type == null &&
    snap.lineSnap != null &&
    snap.lineSnap.distanceFrom(snap.unsnappedPoint) <= getSnappingThreshold()
  ) {
    return { type: 'line', base: snap.lineSnap.toObject() };
  }

  return { type: 'point', point: pointSnap.point.toObject() };
}

/** Returns `true` if the given `MousePositionSnapType` should be treated as a point during in-scene work line
 *    insertion. For example, grid and projected point snaps aren't relevant when defining a point-to-point line.
 */
function isTrueEntityPointSnapForWorkLineInsertion(type: MousePositionSnapType | null): boolean {
  switch (type) {
    case 'endpoint':
    case 'extension':
    case 'intersection':
    case 'midpoint':
      return true;

    case null:
    case 'angle':
    case 'distance':
    case 'grid':
    case 'nearest':
    case 'projected':
      return false;

    default:
      assertExhaustiveSwitch(type);
  }
}

/**
 * During basic member insertion:
 *
 * - line + point on entity -> perpendicular (through that point)
 * - line + same line -> exact (a line lying exactly on top of the clicked line)
 * - line + parallel line -> division (midline between the two)
 * - line + point in space -> parallel (offset = snapped perpendicular distance)
 * - point + point on entity -> point-to-point
 * - point + point in space -> point-angle
 */
export function inferInsertionWorkLine(
  firstSelection: WorkLineInsertionSelection,
  snap: WorkLineSnap,
  settings: WorkLineInsertionSettings,
  snappingLevel: SnappingLevel,
): InsertionWorkLine | null {
  const snappedMousePosition = normalizePointSnapForWorkLineInsertion(snap.pointSnap, snap.unsnappedPoint);
  const cursor = snappedMousePosition.point;

  switch (firstSelection.type) {
    case 'line': {
      const baseLine = EntitySnapTarget.fromObject(firstSelection.base).getDefiningLine();

      if (isTrueEntityPointSnapForWorkLineInsertion(snappedMousePosition.type)) {
        return { type: 'perpendicular', base: firstSelection.base, point: cursor.toObject() };
      }

      if (snap.lineSnap != null && snap.lineSnap.distanceFrom(cursor) <= getSnappingThreshold()) {
        const otherLine = snap.lineSnap.getDefiningLine();

        // two long lines with the same base point that diverge just under the angle tolerance might be "equal" even
        //   though they sit far apart where the cursor is clicked; to rule this out we sanity check with the cursor
        if (otherLine.equals(baseLine) && cursor.distanceToLine(baseLine) <= getSnappingThreshold()) {
          return { type: 'exact', base: firstSelection.base };
        }

        if (otherLine.isParallelTo(baseLine)) {
          return {
            type: 'division',
            base: firstSelection.base,
            other: snap.lineSnap.toObject(),
            quantity: settings.divisionQuantity,
          };
        }
      }

      const offset = baseLine.getPortSideHalfPlane().signedDistanceTo(cursor);
      const snappedOffset = roundToIncrement(offset, getDistanceSnap(snappingLevel));

      return distanceEquals(snappedOffset, 0)
        ? null
        : { type: 'parallel', base: firstSelection.base, offset: snappedOffset };
    }

    case 'point': {
      const basePoint = firstSelection.point;

      if (cursor.equals(basePoint)) return null;

      if (isTrueEntityPointSnapForWorkLineInsertion(snappedMousePosition.type)) {
        return { type: 'point-to-point', basePoint, endPoint: cursor.toObject(), segment: settings.segmentDesired };
      }

      const angle = snapPointAngleDegrees(basePoint, cursor);
      return { type: 'point-angle', basePoint, angle };
    }

    default:
      assertExhaustiveSwitch(firstSelection);
  }
}

function snapPointAngleDegrees(basePoint: Point2DInterface, cursor: Point2D): number {
  const currentDirection = Direction2D.fromPointObjects(basePoint, cursor);

  return snapAngleToDegreesWithOrthogonalBias(Direction2D.right.angleTo(currentDirection));
}
